import { useSyncExternalStore } from "react";

/**
 * The library item detail page a viewer has open, published for the app shell,
 * which renders above the page and outside its query-param context.
 *
 * The redesigned page (modules) is a set of viewing tabs plus one full-screen
 * editor, the "build" tab, and the shell reads the tab: it collapses its nav
 * and stands down its back arrow only while the editor is up. The previous
 * page (every other type, for now) is a builder surface on every tab, so the
 * shell collapses for it the way it always has, and the tab is not reported —
 * "preview" there is not the read-only canvas the redesigned page means by it.
 *
 * Deliberately not a jotai atom: the detail page mounts under its own jotai
 * `Provider`, so anything it wrote to an atom would land in a store the shell
 * can't read.
 *
 * `null` whenever no detail page is mounted, including while one is still
 * loading — which page it is, and the tab it lands on, are derived from the
 * item, so they are genuinely unknown until the item arrives, and collapsing
 * the nav on a guess reads as a glitch.
 */
export type ActiveLibraryItemView =
  | { page: "redesigned"; mainTab: string }
  | { page: "previous" };

let activeView: ActiveLibraryItemView | null = null;
const listeners = new Set<() => void>();

const viewsEqual = (
  a: ActiveLibraryItemView | null,
  b: ActiveLibraryItemView | null,
): boolean =>
  a === b ||
  (a != null &&
    b != null &&
    a.page === b.page &&
    (a.page !== "redesigned" ||
      b.page !== "redesigned" ||
      a.mainTab === b.mainTab));

export function setActiveLibraryItemView(
  view: ActiveLibraryItemView | null,
): void {
  if (viewsEqual(view, activeView)) return;
  activeView = view;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = (): ActiveLibraryItemView | null => activeView;
const getServerSnapshot = (): ActiveLibraryItemView | null => null;

export function useActiveLibraryItemView(): ActiveLibraryItemView | null {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * The redesigned detail page's active main tab; `null` for the previous page
 * and while nothing is open. Consumers key off "build" (the full-screen
 * editor) and "preview" (the read-only canvas).
 */
export function useActiveLibraryItemMainTab(): string | null {
  const view = useActiveLibraryItemView();
  return view?.page === "redesigned" ? view.mainTab : null;
}

/**
 * Whether the redesigned detail page is the one open. Only its provider ever
 * publishes "redesigned" (behind Ff.LibraryItemOverviewTab), so the builder
 * surfaces it shares with the previous page key their read-only affordances
 * off this and stay exactly as they were everywhere else.
 */
export function useIsRedesignedLibraryItemPage(): boolean {
  return useActiveLibraryItemView()?.page === "redesigned";
}
